/**
 * l2-cart-sync — Capa de sincronización carrito ↔ drawer (estilo HS Cart).
 *
 * Problema: el drawer nativo de Horizon se re-renderiza por "morphing" con los datos
 * que envía el propio tema. Cuando una APP externa (p.ej. Doctor Gift / free-gift) añade
 * o quita líneas del carrito por su cuenta (el regalo Vitalnés), el tema no se entera:
 *   - el drawer muestra un estado viejo (regalo que no aparece / no desaparece)
 *   - el contador (cart-bubble) se descuadra (badge 1 con carrito vacío)
 *
 * Solución: interceptar CUALQUIER mutación de carrito (del tema o de apps) y, tras un
 * pequeño retardo, releer el carrito REAL del servidor (/cart.js) y disparar un
 * CartUpdateEvent con el item_count correcto → el cart-icon fija el contador real y el
 * cart-items-component re-renderiza su sección desde el servidor. Doble comprobación
 * (500 ms y ~1600 ms) para capturar regalos que la app añade con retardo.
 */
import { CartUpdateEvent } from '@theme/events';

const CART_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
let timer = null;
let running = false;
let lastSig = null;

// Firma del carrito: si no cambia, NO re-renderizamos. Esto rompe el bucle con Doctor Gift
// (que observa el DOM del carrito → llama a nuestro hook → re-render → su observer salta →
// vuelve a llamar… bucle que hacía parpadear "GRATIS↔0€" y el contador).
function cartSig(cart) {
  return (
    cart.item_count +
    '|' +
    cart.total_price +
    '|' +
    (cart.items || [])
      .map((i) => i.key + ':' + i.quantity + ':' + i.final_line_price + ':' + (i.selling_plan_allocation ? 1 : 0))
      .join(',')
  );
}

function schedule() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(run, 350);
}

async function run() {
  if (running) return;
  running = true;
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const cart = await res.json();
      const sig = cartSig(cart);
      if (sig !== lastSig) {
        lastSig = sig;
        // Sin `sections`: el cart-items-component re-renderiza su propia sección desde el
        // servidor. `itemCount` = recuento real → el badge se fija correcto. `source` distinto
        // de 'product-form-component' para que el drawer NO se auto-abra.
        document.dispatchEvent(
          new CartUpdateEvent(cart, 'l2-cart-sync', {
            source: 'l2-cart-sync',
            itemCount: cart.item_count,
          })
        );
        // Follow-up acotado: por si una app (regalo) cambia el carrito con retardo.
        // Solo continúa la cadena si vuelve a haber un cambio real (dedupe por firma).
        setTimeout(() => {
          running = false;
          schedule();
        }, 900);
        return;
      }
    }
  } catch (_) {
    /* silencioso: nunca bloquear el carrito */
  }
  running = false;
}

// Inicializa la firma con el carrito actual para no re-renderizar en el primer disparo espurio.
fetch('/cart.js', { headers: { Accept: 'application/json' } })
  .then((r) => (r.ok ? r.json() : null))
  .then((c) => {
    if (c) lastSig = cartSig(c);
  })
  .catch(() => {});

// --- Emular el hook de HS Slide Cart para Doctor Gift / free-gift ---
// La app busca `window.HS_SLIDE_CART_UPDATE` + `.hs-site-cart-popup-layout` para refrescar
// el carrito EN SITIO; si no los encuentra, hace un location.href reload (cierra el drawer
// y te lleva a la PDP). Le damos ese hook enrutado a nuestra re-sincronización → sin recarga.
window.HS_SLIDE_CART_UPDATE = function () {
  schedule();
};

// Algunos flujos de la app despachan estos eventos en vez de recargar: también los atendemos.
['cart:refresh', 'cart:updated', 'theme:cartchanged', 'theme:cart:refresh', 'docapp-cart-change'].forEach(
  (evt) => {
    document.addEventListener(evt, schedule);
    document.documentElement.addEventListener(evt, schedule);
  }
);

// --- Interceptar mutaciones de carrito (fetch) ---
const originalFetch = window.fetch;
if (originalFetch) {
  window.fetch = function () {
    let isCart = false;
    try {
      const a = arguments[0];
      const url = a && a.url ? a.url : a;
      const method = (
        (arguments[1] && arguments[1].method) ||
        (a && a.method) ||
        'GET'
      ).toUpperCase();
      if (typeof url === 'string' && CART_RE.test(url) && method === 'POST') isCart = true;
    } catch (_) {}
    const p = originalFetch.apply(this, arguments);
    if (isCart) {
      p.then(() => schedule()).catch(() => {});
    }
    return p;
  };
}

// --- Interceptar mutaciones de carrito (XHR) ---
if (window.XMLHttpRequest) {
  const proto = XMLHttpRequest.prototype;
  const open = proto.open;
  const send = proto.send;
  proto.open = function (method, url) {
    this.__l2Url = url;
    this.__l2Method = method;
    return open.apply(this, arguments);
  };
  proto.send = function () {
    try {
      if (
        typeof this.__l2Url === 'string' &&
        CART_RE.test(this.__l2Url) &&
        (this.__l2Method || '').toUpperCase() === 'POST'
      ) {
        this.addEventListener('load', () => schedule());
      }
    } catch (_) {}
    return send.apply(this, arguments);
  };
}
